import type { ComponentType } from 'react';
import React, { useState } from 'react';
import type {
  FormikHelpers,
  FormikProps,
  FormikTouched,
} from 'formik/dist/types';
import { mapSubmitErrorsToFields } from 'src/components/AuthNew/errors';
import { Formik, Form as FormikForm } from 'formik';
import type {
  QueryParams,
  RequestStatus,
  SharedState,
} from 'src/components/AuthNew/types';
import { validationSchema } from 'src/components/AuthNew/components/RegistrationForm/validationSchema';
import { useSelector } from 'react-redux';
import {
  getClientCountryCode,
  getClientPhoneMask,
} from 'src/store/client/selectors';
import { mapValuesToSubmit, registrationErrorMap } from '../../helpers';
import type { FormValues, OnSubmit } from '../../types';
import { Content } from '../../../Content';

type ChildrenComponentProps = {
  isMobile?: boolean;
  requestStatus?: RequestStatus;
  errorCodes?: string;
} & FormikProps<FormValues>;

type Props = {
  onSubmit: OnSubmit;
  id?: number;
  initialValues: FormValues;
  initialTouched?: FormikTouched<FormValues>;
  HeaderComponent: ComponentType<ChildrenComponentProps>;
  ContentComponent: ComponentType<ChildrenComponentProps>;
  FooterComponent: ComponentType<ChildrenComponentProps>;
  validateOnMount?: boolean;
  isMobile?: boolean;
  requestStatus: RequestStatus;
  queryParams?: QueryParams;
  sharedState?: SharedState;
};

export const Form = ({
  onSubmit,
  initialValues,
  initialTouched,
  HeaderComponent,
  ContentComponent,
  FooterComponent,
  isMobile,
  requestStatus,
  validateOnMount,
  queryParams,
  sharedState,
}: Props) => {
  const [errorCodes, setErrorCodes] = useState('');

  const handleSubmit = async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>,
  ) => {
    try {
      await onSubmit(mapValuesToSubmit(queryParams)(values));
    } catch (error: any) {
      setErrorCodes(error?.response?.data?.errors[0]);
      const errorCodesArray = error?.response?.data?.errors;
      mapSubmitErrorsToFields(registrationErrorMap)(
        formikHelpers.setFieldError,
      )(errorCodesArray);
    }
  };

  const phoneMaskFromStore = useSelector(getClientPhoneMask);
  const countryCodeFromStore = useSelector(getClientCountryCode);
  const phoneMask = sharedState?.phone_mask || phoneMaskFromStore;
  const countryCode = sharedState?.country || countryCodeFromStore;

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      initialTouched={initialTouched}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema(phoneMask, countryCode)}
      validateOnMount={validateOnMount}
    >
      {(props) => (
        <FormikForm>
          {HeaderComponent && (
            <HeaderComponent
              requestStatus={requestStatus}
              isMobile={isMobile}
              {...props}
            />
          )}
          <Content>
            {ContentComponent && (
              <ContentComponent
                requestStatus={requestStatus}
                isMobile={isMobile}
                errorCodes={errorCodes}
                {...props}
              />
            )}
            {FooterComponent && (
              <FooterComponent
                requestStatus={requestStatus}
                isMobile={isMobile}
                {...props}
              />
            )}
          </Content>
        </FormikForm>
      )}
    </Formik>
  );
};
